import { createSlice, PayloadAction } from "@reduxjs/toolkit"
import { AnalyticsState, Option, TopProductExportData, TopDiscountCodesExportData } from "lib/types/state"

const initialState: AnalyticsState = {
  selectedCampaignGroups: [],
  selectedCampaigns: [],
  selectedDiscountRules: [],
  topProductsFilteredBy: "all",
  topProductsFilteredByType: "variant",
  topProductsSortBy: "quantity",
  topProductsFilteredByHits: 5,
  topCodesFilteredByHits: 5,
  productNames: {},
  discountCodeRuleNames: {},
  discountCodeRuleNamesLoaded: false,
  discountExternalRuleNames: {},
  discountExternalRuleNamesLoaded: false,
  filteredByExcludingTax: true,
  topDiscountFilteredByType: 'codes',
  discountCartRuleNames: {},
  discountCartRuleNamesLoaded: false,
  topProductsExportData: null,
  topDiscountCodesExportData: null,
  topShippingMethodsSortBy: 'quantity' as const,
  topShippingMethodsFilteredByHits: 5
}

function reset(state: AnalyticsState) {
  state.selectedCampaignGroups = initialState.selectedCampaignGroups
  state.selectedCampaigns = initialState.selectedCampaigns
  state.selectedDiscountRules = initialState.selectedDiscountRules
  state.topProductsFilteredBy = initialState.topProductsFilteredBy
  state.topProductsFilteredByType = initialState.topProductsFilteredByType
  state.topProductsSortBy = initialState.topProductsSortBy
  state.topProductsFilteredByHits = initialState.topProductsFilteredByHits
  state.topCodesFilteredByHits = initialState.topCodesFilteredByHits
  state.productNames = initialState.productNames
  state.discountCodeRuleNames = initialState.discountCodeRuleNames
  state.discountCodeRuleNamesLoaded = initialState.discountCodeRuleNamesLoaded
  state.discountExternalRuleNames = initialState.discountExternalRuleNames
  state.discountExternalRuleNamesLoaded = initialState.discountExternalRuleNamesLoaded
  state.discountCartRuleNames = initialState.discountCartRuleNames
  state.discountCartRuleNamesLoaded = initialState.discountCartRuleNamesLoaded
  state.filteredByExcludingTax = initialState.filteredByExcludingTax
  state.topDiscountFilteredByType = initialState.topDiscountFilteredByType
  state.topProductsExportData = initialState.topProductsExportData
  state.topDiscountCodesExportData = initialState.topDiscountCodesExportData
  state.topShippingMethodsSortBy = initialState.topShippingMethodsSortBy
  state.topShippingMethodsFilteredByHits = initialState.topShippingMethodsFilteredByHits
}

export const slice = createSlice({
  name: "analytics",
  initialState: { ...initialState },
  reducers: {
    reset: reset,
    setSelectedCampaignGroups(state, action: PayloadAction<Option[]>) {
      state.selectedCampaignGroups = action.payload
    },
    setSelectedCampaigns(state, action: PayloadAction<Option[]>) {
      state.selectedCampaigns = action.payload
    },
    setSelectedDiscountRules(state, action: PayloadAction<Option[]>) {
      state.selectedDiscountRules = action.payload
    },
    setTopProductsFilteredBy(state, action: PayloadAction<string>) {
      state.topProductsFilteredBy = action.payload
    },
    setTopProductsFilteredByType(state, action: PayloadAction<string>) {
      state.topProductsFilteredByType = action.payload
    },
    setTopProductsSortBy(state, action: PayloadAction<'quantity' | 'totalSales'>) {
      state.topProductsSortBy = action.payload
    },
    setTopProductsFilteredByHits(state, action: PayloadAction<number>) {
      state.topProductsFilteredByHits = action.payload
    },
    setTopCodesFilteredByHits(state, action: PayloadAction<number>) {
      state.topCodesFilteredByHits = action.payload
    },
    setProductNames(state, action: PayloadAction<{ [productId: string]: string; }>) {
      state.productNames = action.payload
    },
    setFilteredByExcludingTax(state, action: PayloadAction<boolean>) {
      state.filteredByExcludingTax = action.payload
    },
    setTopDiscountFilteredByType(state, action: PayloadAction<'codes' | 'code rules' | 'external rules' | 'cart rules'>) {
      state.topDiscountFilteredByType = action.payload
    },
    setDiscountCodeRuleNames(state, action: PayloadAction<{ [ruleId: string]: string; }>) {
      state.discountCodeRuleNames = action.payload
      state.discountCodeRuleNamesLoaded = true
    },
    setDiscountExternalRuleNames(state, action: PayloadAction<{ [ruleId: string]: string; }>) {
      state.discountExternalRuleNames = action.payload
      state.discountExternalRuleNamesLoaded = true
    },
    setDiscountCartRuleNames(state, action: PayloadAction<{ [ruleId: string]: string; }>) {
      state.discountCartRuleNames = action.payload
      state.discountCartRuleNamesLoaded = true
    },
    setTopProductsExportData(state, action: PayloadAction<TopProductExportData>) {
      state.topProductsExportData = action.payload
    },
    setTopDiscountCodesExportData(state, action: PayloadAction<TopDiscountCodesExportData>) {
      state.topDiscountCodesExportData = action.payload
    },
    setTopShippingMethodsSortBy(state, action: PayloadAction<'quantity' | 'totalSales'>) {
      state.topShippingMethodsSortBy = action.payload
    },
    setTopShippingMethodsFilteredByHits(state, action: PayloadAction<number>) {
      state.topShippingMethodsFilteredByHits = action.payload
    }
  }
})

const { actions, reducer } = slice
export const {
  setSelectedCampaignGroups,
  setSelectedCampaigns,
  setSelectedDiscountRules,
  setTopProductsFilteredBy,
  setTopProductsFilteredByType,
  setTopProductsSortBy,
  setTopProductsFilteredByHits,
  setTopCodesFilteredByHits,
  setProductNames,
  setFilteredByExcludingTax,
  setTopDiscountFilteredByType,
  setDiscountCodeRuleNames,
  setDiscountExternalRuleNames,
  setDiscountCartRuleNames,
  setTopProductsExportData,
  setTopDiscountCodesExportData,
  setTopShippingMethodsSortBy,
  setTopShippingMethodsFilteredByHits
} = actions

export default reducer
